import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UserService } from '../../../services/user/user.service';
import * as alertifyjs from 'alertifyjs';

// Interfaces según la documentación del endpoint GET /api/fees/version/:version_id
interface VersionDetail {
  _id: string;
  status: boolean;
  paused: boolean;
  code: string;
  code_concept: string;
  description: string;
  name: string;
  date: string;
  account_payment_list: string[];
  user: {
    _id: string;
    name: string;
    email: string;
  };
  fee: {
    application_type: {
      _id: string;
      name: string;
      description: string;
    };
    frequency: {
      _id: string;
      name: string;
      days: number;
    };
    unit_of_measure: string;
    account_payment: {
      _id: string;
      name: string;
      payment_method: {
        name: string;
        bank: {
          name: string;
        };
      };
    };
    limit: string;
  };
  user_edit?: {
    _id: string;
    name: string;
    email: string;
  };
  observation_edit?: string;
  edited_at?: string;
  action_type: 'CREATED' | 'EDITED' | 'DELETED';
  action_user: {
    _id: string;
    name: string;
    email: string;
  };
  action_date: string;
  observation: string;
  fees: VersionFee[];
}

interface VersionFee {
  _id: string;
  code: string;
  concept_deduction: string;
  attribute: string;
  condition: string;
  value: number;
  commission_type: string;
  percentage: number;
  fixed_amount: number;
  status: string;
  applies_reactivation: boolean;
  applies_update_recalc: boolean;
}

@Component({
  selector: 'app-version-detail',
  templateUrl: './version-detail.component.html',
  styleUrls: ['./version-detail.component.css']
})
export class VersionDetailComponent implements OnChanges {
  @Input() versionId: string = '';
  @Input() isVisible: boolean = false;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() backToTimelineEvent = new EventEmitter<void>();

  // Propiedades del componente
  versionDetail: VersionDetail | null = null;
  loading: boolean = false;
  error: string = '';
  showRules: boolean = false;

  constructor(private _user: UserService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['versionId'] && this.versionId && this.isVisible) {
      this.fetchVersionDetail();
    }
    if (changes['isVisible'] && this.isVisible && this.versionId) {
      this.fetchVersionDetail();
    }
  }

  // Método para obtener los detalles de la versión
  fetchVersionDetail = async () => {
    if (!this.versionId) return;
    
    try {
      this.loading = true;
      this.error = '';
      
      // Llamada al endpoint según documentación: GET /api/fees/version/:version_id
      const response = await this._user.getVersionDetail(this.versionId);
      
      if (response.success) {
        this.versionDetail = response.data;
      } else {
        throw new Error(response.error || 'Error desconocido');
      }
      
    } catch (error: any) {
      console.error('Error fetching version detail:', error);
      this.error = error.message || 'Error al cargar los detalles de la versión';
      alertifyjs.error('Error al cargar los detalles de la versión');
    } finally {
      this.loading = false;
    }
  };

  // Método para cerrar el modal
  close = () => {
    this.isVisible = false;
    this.versionDetail = null;
    this.error = '';
    this.showRules = false;
    this.closeEvent.emit();
  };

  // Método para volver al timeline
  backToTimeline = () => {
    this.isVisible = false;
    this.versionDetail = null;
    this.error = '';
    this.showRules = false;
    this.backToTimelineEvent.emit();
  };

  // Método para formatear fechas
  formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Método para obtener iniciales del usuario
  getUserInitials = (name: string): string => {
    if (!name) return 'N/A';
    
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  // Método para formatear el display de la cuenta contable
  getAccountDisplay = (account: any): string => {
    if (!account) return 'No asignada';
    
    if (account.name && account.payment_method?.name) {
      return `${account.name} - ${account.payment_method.name}`;
    } else if (account.name) {
      return account.name;
    }
    
    return 'Cuenta no válida';
  };

  // Método para formatear moneda
  formatCurrency = (amount: number, currency: string = 'USD'): string => {
    if (amount === null || amount === undefined) return 'N/A';
    
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  // Método para formatear porcentaje
  formatPercentage = (percentage: number): string => {
    if (percentage === null || percentage === undefined) return 'N/A';
    
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(percentage / 100);
    } catch (error) {
      return `${percentage}%`;
    }
  };

  // Método para obtener el texto del tipo de acción
  getActionTypeText = (actionType: string): string => {
    const actionTypes: { [key: string]: string } = {
      'CREATED': 'Creado',
      'EDITED': 'Editado',
      'DELETED': 'Eliminado',
      'ACTIVATED': 'Activado',
      'DEACTIVATED': 'Desactivado',
      'PAUSED': 'Pausado',
      'RESUMED': 'Reanudado'
    };
    
    return actionTypes[actionType] || actionType;
  };

  // Método para obtener el color del tipo de acción
  getActionTypeColor = (actionType: string): string => {
    const actionColors: { [key: string]: string } = {
      'CREATED': 'success',
      'EDITED': 'warning',
      'DELETED': 'danger',
      'ACTIVATED': 'success',
      'DEACTIVATED': 'danger',
      'PAUSED': 'warning',
      'RESUMED': 'info'
    };
    
    return actionColors[actionType] || 'secondary';
  };

  // Método para obtener el icono del tipo de acción
  getActionTypeIcon = (actionType: string): string => {
    const actionIcons: { [key: string]: string } = {
      'CREATED': 'fas fa-plus-circle',
      'EDITED': 'fas fa-edit',
      'DELETED': 'fas fa-trash-alt',
      'ACTIVATED': 'fas fa-check-circle',
      'DEACTIVATED': 'fas fa-times-circle',
      'PAUSED': 'fas fa-pause-circle',
      'RESUMED': 'fas fa-play-circle'
    };
    
    return actionIcons[actionType] || 'fas fa-circle';
  };

  // Método para determinar si una fee tiene condiciones complejas
  hasComplexConditions = (fee: VersionFee): boolean => {
    return fee.condition === 'BETWEEN';
  };

  // Método para obtener el texto de la condición
  getConditionText = (fee: VersionFee): string => {
    if (!fee.condition || !fee.attribute) {
      return 'Sin condiciones específicas';
    }

    const attribute = fee.attribute;
    const operator = fee.condition;
    
    if (fee.condition === 'BETWEEN') {
      return `${attribute} ${operator} ${fee.value}`;
    } else {
      return `${attribute} ${operator} ${fee.value}`;
    }
  };

  // Método para obtener el texto de la tarifa
  getTariffText = (fee: VersionFee): string => {
    const parts: string[] = [];
    
    if (fee.fixed_amount && fee.fixed_amount > 0) {
      parts.push(`$${fee.fixed_amount.toFixed(2)}`);
    }
    
    if (fee.percentage && fee.percentage > 0) {
      parts.push(`${fee.percentage}%`);
    }
    
    if (parts.length === 0) {
      return 'Sin tarifa configurada';
    }
    
    return parts.join(' + ');
  };

  // Método para verificar si una fee está activa
  isFeeActive = (fee: VersionFee): boolean => {
    return fee.status === 'ACTIVE';
  };

  // Método para obtener las aplicaciones especiales de una fee
  getSpecialApplications = (fee: VersionFee): string[] => {
    const applications: string[] = [];
    
    if (fee.applies_reactivation) {
      applications.push('Reactivación');
    }
    
    if (fee.applies_update_recalc) {
      applications.push('Recálculo');
    }
    
    return applications;
  };

  // Método para alternar la visibilidad de las reglas
  toggleRules = () => {
    this.showRules = !this.showRules;
  };
} 